import clsx from 'clsx'
import parse from 'html-react-parser'
import React, { CSSProperties, ReactElement, useEffect, useState } from 'react'
import { Control } from 'react-hook-form'

import s from './styles.module.scss'

import TagPriceSeasonPrices from './season-prices/tag-price-season-prices/SeasonPrices'
import TagNabooPack from './tag-naboo-pack/TagNabooPack'
import TagPrice from './tag-price/TagPrice'
import { useDebounced } from '../../../../../../hooks/useDebounced'
import {
  ClientPricingItemOutputDto,
  SeasonEnum,
  ServiceProviderCategoryEnum,
  ServiceProvisionUnitEnum,
} from '../../../../../../lib/graphql'
import {
  decimalPrice,
  roundedPrice,
} from '../../../../../../utils/currencyFormatter'
import { formatAdultsRange } from '../../../../../../utils/format-adults-range'
import { getPriceFromToggleStatus } from '../../../../../../utils/toggle-status'
import SwitchButton from '../../../../../atoms/fields/switch-button/SwitchButton'
import VatLabel from '../../../../../atoms/fields/vat-label/VatLabel'
import Icon from '../../../../../atoms/icons/icon/Icon'
import IconSvg from '../../../../../atoms/icons/icon-svg/IconSvg'
import { useCurrencyAreaContext } from '../../../../../contexts'
import { usePriceContext } from '../../../../../contexts/price-context/usePriceContext'
import Checkbox from '../../../../../design-system/form/Checkbox'
import { NumberInputRange } from '../../../../../forms/number-input-range/number-input-range'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../molecules/popover/Popover'
import { Rating } from '../../../../../molecules/rating/Rating'
import { ServiceProvisionImage } from '../../../../../molecules/service-provision-image/ServiceProvisionImage'

const getSelectedPriceId = (clientPricingItem: ClientPricingItemOutputDto) =>
  clientPricingItem.option?.priceId ??
  clientPricingItem.objectData?.prices?.at(0)?._id ??
  ''

const TagClientPricingItem = ({
  clientPricingItem,
  extraContent,
  className,
  disabled,
  selectPrestation,
  selectPrestationSwitch,
  hasRoundedPrice = true,
  styleContent = {},
  maxNumberInputRange,
  onClick,
  isEditableServiceProvision,
  hasDebouncedQuantity = true,
  showPrice = true,
  showTotalPrice = true,
  isApp = false,
  hideDescription = false,
  label,
  isCheckbox,
  control,
  style,
  beforeQuantityOnClickCallback,
  hideQuantitySelector = false,
  onClickDuplicate,
}: {
  clientPricingItem: ClientPricingItemOutputDto
  extraContent?: ReactElement
  className?: string
  disabled?: boolean
  selectPrestation?: {
    onSelectPrestation?: (quantity?: number, priceId?: string) => void
    onDelete?: (clientPricingItemId: string) => void
    minQuantity?: number
  }
  selectPrestationSwitch?: {
    value: boolean
    setValue: (value: boolean) => void
  }
  isCheckbox?: boolean
  hasRoundedPrice?: boolean
  styleContent?: React.CSSProperties
  maxNumberInputRange?: number
  onClick?: () => void
  isEditableServiceProvision?: boolean
  hasDebouncedQuantity?: boolean
  showPrice?: boolean
  showTotalPrice?: boolean
  isApp?: boolean
  hideDescription?: boolean
  label?: string
  control?: Control<any, any>
  style?: CSSProperties
  beforeQuantityOnClickCallback?: (quantity: number) => void
  hideQuantitySelector?: boolean
  onClickDuplicate?: () => void
}) => {
  const [displayDescription, setDisplayDescription] = useState<boolean>(false)
  const [selectedPriceId, setSelectedPriceId] = useState<string>(
    getSelectedPriceId(clientPricingItem),
  )
  const [quantity, setQuantity] = useState<number>(
    clientPricingItem.priceOption.quantity,
  )
  const debouncedQuantity = useDebounced<number>(
    quantity,
    hasDebouncedQuantity ? 400 : 0,
  )

  const toggleStatus = usePriceContext()
  const { currency } = useCurrencyAreaContext()

  const price = getPriceFromToggleStatus({
    priceOption: clientPricingItem.totalPrice,
    toggleStatus,
    total: false,
    discountPrices: false,
  })

  useEffect(() => {
    if (
      (clientPricingItem.priceOption.quantity !== debouncedQuantity ||
        clientPricingItem.option.priceId !== selectedPriceId) &&
      selectPrestation?.onSelectPrestation
    ) {
      selectPrestation.onSelectPrestation(quantity, selectedPriceId)
    }
  }, [debouncedQuantity, selectedPriceId])

  useEffect(() => {
    setSelectedPriceId(getSelectedPriceId(clientPricingItem))

    //TXES clientPricingItems quantities are set dynamically based on nights selected
    // if (clientPricingItem.type === ClientPricingItemTypeEnum.TAXES) setQuantity(clientPricingItem.priceOption.quantity)
    setQuantity(clientPricingItem.priceOption.quantity)
  }, [clientPricingItem.priceOption.quantity, clientPricingItem.option.priceId])

  const getPriceToDisplay = () => {
    return clientPricingItem.objectData?.prices?.find(
      (price) => selectedPriceId === price._id,
    )
  }

  const hasExtraContent = () => {
    //hide description if props hideDescription is true
    if (hideDescription) {
      return false
    }

    //if serviceProvision has extraContent & description, return true
    if (extraContent && clientPricingItem.objectData?.description) {
      return true
    }

    //if serviceProvision has description, return true
    if (clientPricingItem.objectData?.description) {
      return true
    }

    //if serviceProvision has seasonPrices, return true
    if (clientPricingItem.objectData?.seasonPrices) {
      return true
    }

    //if not startingFees mode and prices to display
    if (
      !clientPricingItem.objectData?.startingFees &&
      (clientPricingItem.objectData?.prices || []).length > 1
    ) {
      return true
    }

    //by default, return false
    return false
  }

  return (
    <div style={style} className={clsx(className, s.container)}>
      <div className={s.tagServiceProvision}>
        {isCheckbox && control && (
          <div className={s.labelContainer}>
            <Checkbox
              label={
                <div className='flex gap-1'>
                  {label}
                  <VatLabel />
                </div>
              }
              name={`clientPricingItems.${clientPricingItem._id}.isSelectedHostOption`}
              rules={{}}
              disabled={disabled}
              control={control}
            />
          </div>
        )}
        {!isCheckbox && (
          <>
            {label ? (
              <div className={s.labelContainer}>
                <p dangerouslySetInnerHTML={{ __html: label }} />
                <VatLabel />
              </div>
            ) : (
              <div
                className={s.content}
                style={styleContent}
                onClick={(event) => {
                  if (onClick) {
                    onClick()
                  } else {
                    if (!extraContent) {
                      event.stopPropagation()
                    }
                    if (hasExtraContent()) {
                      setDisplayDescription(!displayDescription)
                    }
                  }
                }}
              >
                <div className={s.provisionInformation}>
                  <div className={s.name}>
                    {!disabled && selectPrestation?.onDelete && (
                      <div className={s.iconWrapper}>
                        <span
                          className={s.deleteCross}
                          onClick={(e) => {
                            if (selectPrestation?.onDelete) {
                              e.stopPropagation()
                              selectPrestation.onDelete(clientPricingItem._id)
                            }
                          }}
                        >
                          <Icon name='cross-without-circle' color='white' />
                        </span>
                      </div>
                    )}
                    {[
                      ServiceProviderCategoryEnum.ACTIVITY,
                      ServiceProviderCategoryEnum.RESTAURATION,
                      ServiceProviderCategoryEnum.TRANSPORT,
                    ].includes(clientPricingItem.category) && (
                      <ServiceProvisionImage
                        serviceCategory={clientPricingItem.category}
                        uri={
                          clientPricingItem.objectData
                            ?.serviceProvisionMainPicture?.uri
                        }
                        width={60}
                        height={60}
                      />
                    )}
                    <div className={s.serviceInformations}>
                      <div>{clientPricingItem.objectData?.label}</div>
                      <div className={s.serviceNameAndRating}>
                        <div className={s.serviceName}>
                          {clientPricingItem.serviceName}
                        </div>
                        {clientPricingItem?.nabooRating?.rating && (
                          <>
                            <div>|</div>
                            <Rating
                              rating={clientPricingItem?.nabooRating?.rating}
                              userRatingsTotal={
                                clientPricingItem?.nabooRating?.ratingsTotal
                              }
                              isNabooRating
                              small
                            />
                          </>
                        )}
                      </div>
                      {clientPricingItem.objectData?.description &&
                        (displayDescription ? (
                          <div className={clsx(s.containerDetails)}>
                            <div className={s.description}>
                              {parse(clientPricingItem.objectData.description)}
                            </div>

                            {extraContent && (
                              <div className={s.details}>{extraContent}</div>
                            )}

                            {clientPricingItem.objectData?.seasonPrices && (
                              <TagPriceSeasonPrices
                                seasonPrices={
                                  clientPricingItem.objectData?.seasonPrices
                                }
                              />
                            )}

                            {clientPricingItem.objectData?.prices.some(
                              (price) => price.season,
                            ) ? (
                              <TagPriceSeasonPrices
                                seasonPrices={{
                                  [SeasonEnum.LOW]:
                                    clientPricingItem.objectData?.prices.find(
                                      (price) =>
                                        price.season === SeasonEnum.LOW,
                                    )?.price,
                                  [SeasonEnum.MEDIUM]:
                                    clientPricingItem.objectData?.prices.find(
                                      (price) =>
                                        price.season === SeasonEnum.MEDIUM,
                                    )?.price,
                                  [SeasonEnum.HIGH]:
                                    clientPricingItem.objectData?.prices.find(
                                      (price) =>
                                        price.season === SeasonEnum.HIGH,
                                    )?.price,
                                }}
                              />
                            ) : (
                              !clientPricingItem.objectData?.startingFees &&
                              (clientPricingItem.objectData?.prices || [])
                                .length > 1 && (
                                <div className={s.adultsRangeContainer}>
                                  {clientPricingItem.objectData?.prices &&
                                    clientPricingItem.objectData.prices
                                      .sort((a, b) => {
                                        if (a.minAdults && b.minAdults) {
                                          return a.minAdults - b.minAdults
                                        }
                                        if (a.minAdults) {
                                          return 1
                                        }
                                        if (b.minAdults) {
                                          return -1
                                        }
                                        return 0
                                      })
                                      .map((serviceProvisionPrice) => {
                                        return (
                                          <div
                                            key={serviceProvisionPrice._id}
                                            className={clsx(s.tagHT, {
                                              [s.selected]:
                                                serviceProvisionPrice._id ===
                                                selectedPriceId,
                                            })}
                                            onClick={(e) => {
                                              if (disabled) {
                                                return
                                              }
                                              e.stopPropagation()
                                              setSelectedPriceId(
                                                serviceProvisionPrice._id,
                                              )
                                            }}
                                          >
                                            {formatAdultsRange(
                                              serviceProvisionPrice.minAdults ??
                                                undefined,
                                              serviceProvisionPrice.maxAdults ??
                                                undefined,
                                            )}
                                          </div>
                                        )
                                      })}
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <div className={s.partialServiceDescription}>
                            <div className={s.description}>
                              {parse(clientPricingItem.objectData.description)}
                            </div>
                          </div>
                        ))}
                    </div>
                    {(selectPrestation?.minQuantity ?? 0) > 0 && (
                      <TagNabooPack />
                    )}
                  </div>

                  {showPrice && (
                    <TagPrice
                      isApp={isApp}
                      priceObject={
                        clientPricingItem.objectData?.startingFees?.price ??
                        getPriceToDisplay()?.price
                      }
                      unit={
                        clientPricingItem.objectData?.startingFees?.unit ??
                        getPriceToDisplay()?.unit ??
                        ServiceProvisionUnitEnum.INDIVIDUAL
                      }
                      hasRoundedPrice={hasRoundedPrice}
                      isStartingFees={
                        !!clientPricingItem.objectData?.startingFees
                      }
                      ownerFeesRate={
                        clientPricingItem.priceOption.fees?.ownerFeesRate ??
                        undefined
                      }
                    />
                  )}

                  <>
                    {isEditableServiceProvision ? (
                      <div className={s.containerAction}>
                        <IconSvg.Edit className={s.edit} />
                      </div>
                    ) : (
                      <>
                        {hasExtraContent() && (
                          <div
                            className={clsx(
                              s.containerAction,
                              s.containerArrow,
                            )}
                          >
                            <Icon
                              name='arrow-down'
                              size={12}
                              className={clsx(s.arrow, {
                                [s.arrowUp]: displayDescription,
                              })}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                </div>
              </div>
            )}
          </>
        )}

        {selectPrestation && !hideQuantitySelector && (
          <div className={s.blockPrice}>
            {onClickDuplicate && (
              <Popover>
                <PopoverTrigger>
                  <IconSvg.IconDots className={s.iconDots} />
                </PopoverTrigger>
                <PopoverContent>
                  <div className={s.contentMenu} onClick={onClickDuplicate}>
                    <div className={s.label}>
                      Dupliquer
                      {clientPricingItem.category ===
                      ServiceProviderCategoryEnum.BATCH_PROVISIONS
                        ? ' le forfait'
                        : ' la prestation'}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            <NumberInputRange
              onChange={(value) => {
                if (value !== undefined) {
                  if (beforeQuantityOnClickCallback) {
                    beforeQuantityOnClickCallback(value)
                  }
                  setQuantity(value)
                }
              }}
              value={debouncedQuantity}
              textField={{
                placeholder: 'Écrire ici',
                style: { width: '100px' },
              }}
              disabled={disabled}
              max={maxNumberInputRange}
              min={selectPrestation.minQuantity ?? 0}
            />
          </div>
        )}
        {showTotalPrice && (
          <span className={s.total}>
            {hasRoundedPrice
              ? roundedPrice(price, { currency })
              : decimalPrice(price, { currency })}
          </span>
        )}
        {selectPrestationSwitch && (
          <div className={s.switchButton}>
            <SwitchButton
              value={selectPrestationSwitch.value}
              setValue={selectPrestationSwitch.setValue}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default TagClientPricingItem
